import { available, checkedAddU128, isU64, isU256, unavailable } from './integer.js';
import type { PoolMathResult } from './types.js';

const CANONICAL_UNSIGNED_INTEGER = /^(?:0|[1-9]\d*)$/;
const MICRO_PERCENT_SCALE = 100_000_000n;

export interface PrizePoolOutcomeInput {
  readonly outcomeIndex: number;
  readonly label: string;
  readonly latestPoolQuoteD6: string | null | undefined;
  readonly virtualQuoteD6: string | null | undefined;
  readonly claimRightsSupplyD6: string | null | undefined;
}

export interface PrizePoolHoldingInput {
  readonly outcomeIndex: number;
  readonly claimRightsBalanceD6: string | null | undefined;
}

export interface PrizePoolPayoutInput {
  readonly prizePoolD6: string | null | undefined;
  readonly outcomes: readonly PrizePoolOutcomeInput[];
  readonly holdings: readonly PrizePoolHoldingInput[];
}

export interface PrizePoolPayoutEstimate {
  readonly outcomeIndex: number;
  readonly label: string;
  readonly claimRightsBalanceD6: bigint;
  readonly claimRightsSupplyD6: bigint;
  /** Percentage magnitude scaled to millionths: 25,000,000 = 25%. */
  readonly claimantShareMicroPercent: number;
  readonly projectedPoolD6: bigint;
  readonly estimatedPayoutD6: bigint;
}

interface ParsedOutcome {
  readonly outcomeIndex: number;
  readonly label: string;
  readonly realQuoteReserveD6: bigint;
  readonly claimRightsSupplyD6: bigint;
}

/**
 * Estimate each held outcome's claimant-pool payout from one coherent market
 * detail + positions snapshot. Every wire integer is parsed strictly and all
 * intermediate domains are checked before multiply-then-divide floor math.
 */
export function estimatePrizePoolPayouts(
  input: PrizePoolPayoutInput,
): PoolMathResult<readonly PrizePoolPayoutEstimate[]> {
  const prizePoolD6 = parseU64(input.prizePoolD6);
  if (prizePoolD6 === null) return unavailable('invalid_prize_pool_input');

  const parsedOutcomes = parseOutcomes(input.outcomes);
  if (parsedOutcomes === null) return unavailable('invalid_prize_pool_input');

  const balances = aggregateHoldings(input.holdings, parsedOutcomes);
  if (!balances.available) return balances;

  let totalRealQuoteReserveD6 = 0n;
  for (const outcome of parsedOutcomes) {
    const next = checkedAddU128(totalRealQuoteReserveD6, outcome.realQuoteReserveD6);
    if (!next.available) return unavailable('prize_pool_estimate_overflow');
    totalRealQuoteReserveD6 = next.value;
  }

  const estimates: PrizePoolPayoutEstimate[] = [];
  for (const outcome of parsedOutcomes) {
    const claimRightsBalanceD6 = balances.value.get(outcome.outcomeIndex) ?? 0n;
    if (claimRightsBalanceD6 === 0n) continue;
    if (outcome.claimRightsSupplyD6 === 0n) return unavailable('zero_claim_rights_supply');
    if (claimRightsBalanceD6 > outcome.claimRightsSupplyD6) {
      return unavailable('invalid_prize_pool_input');
    }

    const otherOutcomeReservesD6 = totalRealQuoteReserveD6 - outcome.realQuoteReserveD6;
    const projectedPool = checkedAddU128(prizePoolD6, otherOutcomeReservesD6);
    if (!projectedPool.available) return unavailable('prize_pool_estimate_overflow');

    const payoutNumeratorD6 = claimRightsBalanceD6 * projectedPool.value;
    if (!isU256(payoutNumeratorD6)) return unavailable('prize_pool_estimate_overflow');

    const shareNumerator = claimRightsBalanceD6 * MICRO_PERCENT_SCALE;
    if (!isU256(shareNumerator)) return unavailable('prize_pool_estimate_overflow');
    const shareMicroPercent = shareNumerator / outcome.claimRightsSupplyD6;
    const claimantShareMicroPercent = Number(shareMicroPercent);
    if (!Number.isSafeInteger(claimantShareMicroPercent)) {
      return unavailable('prize_pool_estimate_overflow');
    }

    estimates.push({
      outcomeIndex: outcome.outcomeIndex,
      label: outcome.label,
      claimRightsBalanceD6,
      claimRightsSupplyD6: outcome.claimRightsSupplyD6,
      claimantShareMicroPercent,
      projectedPoolD6: projectedPool.value,
      estimatedPayoutD6: payoutNumeratorD6 / outcome.claimRightsSupplyD6,
    });
  }

  return available(estimates);
}

function parseOutcomes(
  outcomes: readonly PrizePoolOutcomeInput[],
): readonly ParsedOutcome[] | null {
  const seen = new Set<number>();
  const parsed: ParsedOutcome[] = [];
  for (const outcome of outcomes) {
    if (!isOutcomeIndex(outcome.outcomeIndex) || seen.has(outcome.outcomeIndex)) return null;
    seen.add(outcome.outcomeIndex);

    const latestPoolQuoteD6 = parseU64(outcome.latestPoolQuoteD6);
    const virtualQuoteD6 = parseU64(outcome.virtualQuoteD6);
    const claimRightsSupplyD6 = parseU64(outcome.claimRightsSupplyD6);
    if (
      latestPoolQuoteD6 === null ||
      virtualQuoteD6 === null ||
      claimRightsSupplyD6 === null ||
      latestPoolQuoteD6 < virtualQuoteD6
    ) {
      return null;
    }

    parsed.push({
      outcomeIndex: outcome.outcomeIndex,
      label: outcome.label,
      realQuoteReserveD6: latestPoolQuoteD6 - virtualQuoteD6,
      claimRightsSupplyD6,
    });
  }
  return parsed;
}

function aggregateHoldings(
  holdings: readonly PrizePoolHoldingInput[],
  outcomes: readonly ParsedOutcome[],
): PoolMathResult<ReadonlyMap<number, bigint>> {
  const outcomeIndices = new Set(outcomes.map((outcome) => outcome.outcomeIndex));
  const balances = new Map<number, bigint>();
  for (const holding of holdings) {
    if (!isOutcomeIndex(holding.outcomeIndex)) return unavailable('invalid_prize_pool_input');
    const balance = parseU64(holding.claimRightsBalanceD6);
    if (balance === null) return unavailable('invalid_prize_pool_input');
    if (balance === 0n) continue;
    if (!outcomeIndices.has(holding.outcomeIndex)) return unavailable('invalid_prize_pool_input');

    const next = checkedAddU128(balances.get(holding.outcomeIndex) ?? 0n, balance);
    if (!next.available) return unavailable('prize_pool_estimate_overflow');
    balances.set(holding.outcomeIndex, next.value);
  }
  return available(balances);
}

function parseU64(value: string | null | undefined): bigint | null {
  if (typeof value !== 'string' || !CANONICAL_UNSIGNED_INTEGER.test(value)) return null;
  const parsed = BigInt(value);
  return isU64(parsed) ? parsed : null;
}

function isOutcomeIndex(value: number): boolean {
  return Number.isSafeInteger(value) && value >= 0;
}
